import { useState, useEffect, useCallback } from "react";

const api = {
  get: (url) => fetch(url).then(r => r.ok ? r.json() : Promise.reject(r.status)),
  post: (url, body) => fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(r => r.ok ? r.json() : Promise.reject(r.status)),
  put: (url, body) => fetch(url, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(r => r.ok ? r.json() : Promise.reject(r.status)),
  del: (url) => fetch(url, { method: 'DELETE' }).then(r => r.ok ? r.json() : Promise.reject(r.status)),
};

const fmt = (n) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n || 0);
const uid = () => Date.now() + Math.random().toString(36).slice(2);

// ── ACET Brand Colours ────────────────────────────────────────────────────────
// Navy: #1a2744  Gold: #f5a800  Mid-navy: #243156  Light gold: #ffc235
// White: #ffffff  Grey text: #6b7a99  Light bg: #f4f6fb  Border: #d0d8ea

export default function App() {
  const [user, setUser] = useState(null);
  const [view, setView] = useState("home");
  const [selectedAcademy, setSelectedAcademy] = useState(null);
  const [urlError] = useState(() => new URLSearchParams(window.location.search).get('error'));

  useEffect(() => {
    api.get('/api/me')
      .then(u => {
        setUser(u);
        if (!u.isAdmin && u.academies.length === 1) {
          setSelectedAcademy(u.academies[0]);
          setView("academy");
        }
      })
      .catch(status => { if (status === 401) setUser(false); });
  }, []);

  if (user === null) return <LoadingScreen />;
  if (user === false) return <LoginScreen error={urlError} />;
  if (view === "home") return <HomeScreen user={user} onSelectAcademy={(a) => { setSelectedAcademy(a); setView("academy"); }} onAdminClick={() => setView("admin")} />;
  if (view === "admin" && user.isAdmin) return <AdminPanel user={user} onBack={() => setView("home")} />;
  if (view === "academy") return <AcademyView name={selectedAcademy} user={user} onBack={() => setView("home")} showBack={user.isAdmin || user.academies.length > 1} />;
  return null;
}

// ── ACET Logo SVG ─────────────────────────────────────────────────────────────
function AcetLogo({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 60 60" fill="none">
      <polygon points="30,2 58,52 2,52" fill="#1a2744" />
      <polygon points="30,10 52,48 30,48" fill="#f5a800" opacity="0.85" />
      <polygon points="30,22 44,48 16,48" fill="#ffc235" opacity="0.7" />
      <rect x="0" y="54" width="60" height="4" fill="#f5a800" />
    </svg>
  );
}

function LoadingScreen() {
  return (
    <div style={S.loadingWrap}>
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@700;800&family=Open+Sans:wght@400;500;600&display=swap" rel="stylesheet" />
      <AcetLogo size={56} />
      <div style={S.spinner} />
      <p style={{ color: "#6b7a99", marginTop: 16, fontFamily: "'Open Sans', sans-serif", fontSize: 14 }}>Signing you in…</p>
    </div>
  );
}

function LoginScreen({ error }) {
  return (
    <div style={S.loginPage}>
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@700;800&family=Open+Sans:wght@400;500;600&display=swap" rel="stylesheet" />
      <div style={S.loginLeft}>
        <div style={S.loginGeo} />
        <div style={S.loginGeo2} />
        <div style={S.loginLeftContent}>
          <AcetLogo size={64} />
          <h1 style={S.loginBrand}>ACET</h1>
          <p style={S.loginBrandSub}>Aston Community Education Trust</p>
        </div>
      </div>
      <div style={S.loginRight}>
        <div style={S.loginCard}>
          <div style={S.loginCardBadge}>IT Department</div>
          <h2 style={S.loginTitle}>DFC Budget Planner</h2>
          <p style={S.loginSubtitle}>Devolved Formula Capital allocation tool for ACET academies.</p>
          {error && <div style={S.errorBanner}>{error === 'auth_failed' ? 'Authentication failed. Please try again.' : 'Sign-in error. Please try again.'}</div>}
          <a href="/auth/login" style={S.msBtn}><MsIcon />Sign in with Microsoft</a>
          <p style={S.loginNote}>Use your ACET Microsoft 365 account</p>
        </div>
      </div>
    </div>
  );
}

function MsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 21 21" style={{ marginRight: 10, flexShrink: 0 }}>
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

function TopBar({ user, showAdmin, onAdminClick }) {
  return (
    <div style={S.topBar}>
      <div style={S.topBarInner}>
        <div style={S.topBarLeft}>
          <AcetLogo size={36} />
          <div style={S.topBarTitle}>
            <span style={S.topBarACET}>ACET</span>
            <span style={S.topBarSub}>DFC Budget Planner</span>
          </div>
        </div>
        <div style={S.topBarRight}>
          <div style={S.topBarUser}>
            <div style={S.userAvatar}>{user.name.charAt(0).toUpperCase()}</div>
            <span style={S.userName}>{user.name}</span>
          </div>
          {showAdmin && (
            <button style={S.topBarAdminBtn} onClick={onAdminClick}>⚙ Admin</button>
          )}
          <a href="/auth/logout" style={S.topBarLogout}>Sign out</a>
        </div>
      </div>
    </div>
  );
}

function HomeScreen({ user, onSelectAcademy, onAdminClick }) {
  const [hovered, setHovered] = useState(null);
  return (
    <div style={S.page}>
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@700;800&family=Open+Sans:wght@400;500;600&display=swap" rel="stylesheet" />
      <TopBar user={user} showAdmin={user.isAdmin} onAdminClick={onAdminClick} />
      <div style={S.heroStrip}>
        <div style={S.heroGeoLeft} />
        <div style={S.heroGeoRight} />
        <div style={S.heroContent}>
          <div style={S.heroBadge}>Devolved Formula Capital · {user.academicYear}</div>
          <h1 style={S.heroHeading}>Select Your Academy</h1>
          <p style={S.heroDesc}>Choose an academy below to view your DFC allocation and submit a pathway preference.</p>
        </div>
      </div>
      <div style={S.pageContent}>
        <div style={S.academyGrid}>
          {user.academies.map((a, i) => (
            <button key={a}
              style={{ ...S.academyCard, ...(hovered === i ? S.academyCardHover : {}) }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => onSelectAcademy(a)}>
              <div style={S.academyCardAccent} />
              <div style={S.academyCardInitial}>{a.split(" ").map(w => w[0]).join("").slice(0, 2)}</div>
              <div style={S.academyCardName}>{a}</div>
              <div style={S.academyCardArrow}>→</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function AdminPanel({ user, onBack }) {
  const [activeTab, setActiveTab] = useState("configure");
  const [selectedAcademy, setSelectedAcademy] = useState(null);
  const [draft, setDraft] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [config, setConfig] = useState(null);
  const [savedMsg, setSavedMsg] = useState('');
  const [filterAcademy, setFilterAcademy] = useState("All");
  const [resetConfirm, setResetConfirm] = useState(null);

  const flash = (msg) => { setSavedMsg(msg); setTimeout(() => setSavedMsg(''), 2500); };

  const loadAcademy = useCallback(async (name) => {
    setSelectedAcademy(name);
    const data = await api.get(`/api/academy/${encodeURIComponent(name)}`);
    setDraft(data);
  }, []);

  const loadConfig = useCallback(async () => {
    const cfg = await api.get('/api/config');
    // Ensure academies is always an array even if config.json predates this field
    if (!cfg.academies || cfg.academies.length === 0) {
      cfg.academies = [
        "Aston Academy", "Swinton Academy", "Shirebrook Academy",
        "Academy 4", "Academy 5", "Academy 6",
        "Academy 7", "Academy 8", "Academy 9",
        "Academy 10", "Academy 11", "Academy 12",
      ];
    }
    if (!cfg.academicYear) cfg.academicYear = '2025-26';
    setConfig(cfg);
    loadAcademy(cfg.academies[0]);
  }, [loadAcademy]);

  useEffect(() => { loadConfig(); }, []);
  useEffect(() => { api.get('/api/submissions').then(setSubmissions).catch(() => {}); }, []);

  const DEFAULT_ACAD = ["Aston Academy","Swinton Academy","Shirebrook Academy","Academy 4","Academy 5","Academy 6","Academy 7","Academy 8","Academy 9","Academy 10","Academy 11","Academy 12"];
  const academies = config?.academies?.length ? config.academies : DEFAULT_ACAD;

  const saveAcademy = async () => { await api.put(`/api/academy/${encodeURIComponent(selectedAcademy)}`, draft); flash('✓ Saved!'); };
  const saveConfig = async () => { await api.put('/api/config', config); flash('✓ Settings saved!'); };
  const deleteSubmission = async (id) => {
    await api.del(`/api/submissions/${id}`);
    setSubmissions(s => s.filter(x => x.id !== id));
    setResetConfirm(null);
    flash('✓ Submission reset');
  };

  const updateEssential = (id, field, val) => setDraft(d => ({ ...d, essentials: d.essentials.map(e => e.id === id ? { ...e, [field]: field === 'cost' ? parseFloat(val) || 0 : val } : e) }));
  const addEssential = () => setDraft(d => ({ ...d, essentials: [...d.essentials, { id: uid(), label: 'New Essential Item', cost: 0 }] }));
  const removeEssential = (id) => setDraft(d => ({ ...d, essentials: d.essentials.filter(e => e.id !== id) }));
  const updatePathway = (id, field, val) => setDraft(d => ({ ...d, pathways: d.pathways.map(p => p.id === id ? { ...p, [field]: field === 'cost' ? parseFloat(val) || 0 : val } : p) }));
  const addPathway = () => { const L = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"; setDraft(d => ({ ...d, pathways: [...d.pathways, { id: uid(), label: `Option ${L[d.pathways.length] || d.pathways.length + 1}`, description: '', cost: 0 }] })); };
  const removePathway = (id) => setDraft(d => ({ ...d, pathways: d.pathways.filter(p => p.id !== id) }));
  const updateAcademyName = (idx, val) => setConfig(c => { const a = [...c.academies]; a[idx] = val; return { ...c, academies: a }; });
  const addAcademy = () => setConfig(c => ({ ...c, academies: [...c.academies, `Academy ${c.academies.length + 1}`] }));
  const removeAcademy = (idx) => setConfig(c => ({ ...c, academies: c.academies.filter((_, i) => i !== idx) }));

  const totalEssentials = draft ? draft.essentials.reduce((s, e) => s + (e.cost || 0), 0) : 0;
  const remaining = draft ? (draft.dfcAllocation || 0) - totalEssentials : 0;
  const visibleSubmissions = filterAcademy === 'All' ? submissions : submissions.filter(s => s.academy === filterAcademy);

  return (
    <div style={S.page}>
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@700;800&family=Open+Sans:wght@400;500;600&display=swap" rel="stylesheet" />
      <TopBar user={user} showAdmin={false} onAdminClick={() => {}} />
      <div style={S.adminTopBar}>
        <div style={S.adminTopBarInner}>
          <button style={S.backBtn} onClick={onBack}>← Back to academies</button>
          <h2 style={S.adminTopTitle}>Admin Panel</h2>
          {savedMsg && <div style={S.savedToast}>{savedMsg}</div>}
        </div>
        <div style={S.adminTabRow}>
          {["configure", "settings", "access", "submissions"].map(t => (
            <button key={t} style={{ ...S.adminTab, ...(activeTab === t ? S.adminTabActive : {}) }} onClick={() => setActiveTab(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
              {t === 'submissions' && submissions.length > 0 && <span style={S.tabBadge}>{submissions.length}</span>}
            </button>
          ))}
        </div>
      </div>

      {activeTab === "configure" && draft && config && (
        <div style={S.adminBody}>
          <div style={S.adminSidebar}>
            <div style={S.sidebarHead}>Academies</div>
            {academies.map(a => (
              <button key={a} style={{ ...S.sidebarItem, ...(selectedAcademy === a ? S.sidebarItemActive : {}) }} onClick={() => loadAcademy(a)}>{a}</button>
            ))}
          </div>
          <div style={S.adminMain}>
            <div style={S.adminAcademyHeader}>
              <div style={S.adminAcademyAccent} />
              <h3 style={S.adminAcademyTitle}>{selectedAcademy}</h3>
            </div>
            <div style={S.card}>
              <div style={S.cardLabel}>DFC Allocation (£)</div>
              <input style={S.input} type="number" value={draft.dfcAllocation || ''} placeholder="e.g. 45000"
                onChange={e => setDraft(d => ({ ...d, dfcAllocation: parseFloat(e.target.value) || 0 }))} />
            </div>
            <div style={S.card}>
              <div style={S.cardHeaderRow}>
                <div style={S.cardLabel}>Essential Repair Costs</div>
                <button style={S.ghostBtn} onClick={addEssential}>+ Add Item</button>
              </div>
              {draft.essentials.map(e => (
                <div key={e.id} style={S.itemRow}>
                  <input style={{ ...S.input, flex: 3 }} value={e.label} onChange={ev => updateEssential(e.id, 'label', ev.target.value)} placeholder="Item description" />
                  <input style={{ ...S.input, flex: 1 }} type="number" value={e.cost || ''} onChange={ev => updateEssential(e.id, 'cost', ev.target.value)} placeholder="£" />
                  <button style={S.iconBtn} onClick={() => removeEssential(e.id)}>✕</button>
                </div>
              ))}
              <div style={S.totalLine}><span>Total Essentials</span><span style={{ color: '#c0392b', fontWeight: 700 }}>{fmt(totalEssentials)}</span></div>
              <div style={{ ...S.totalLine, borderTop: '2px solid #1a2744' }}>
                <span style={{ fontWeight: 700 }}>Remaining Budget</span>
                <span style={{ color: remaining >= 0 ? '#1a7a4a' : '#c0392b', fontWeight: 800, fontSize: 20 }}>{fmt(remaining)}</span>
              </div>
            </div>
            <div style={S.card}>
              <div style={S.cardHeaderRow}>
                <div style={S.cardLabel}>Budget Pathways</div>
                <button style={S.ghostBtn} onClick={addPathway}>+ Add Option</button>
              </div>
              {draft.pathways.map(p => (
                <div key={p.id} style={S.pathwayEditCard}>
                  <div style={S.itemRow}>
                    <input style={{ ...S.input, flex: 1 }} value={p.label} onChange={ev => updatePathway(p.id, 'label', ev.target.value)} placeholder="Option label" />
                    <input style={{ ...S.input, flex: 1 }} type="number" value={p.cost || ''} onChange={ev => updatePathway(p.id, 'cost', ev.target.value)} placeholder="£ Cost" />
                    <button style={S.iconBtn} onClick={() => removePathway(p.id)}>✕</button>
                  </div>
                  <textarea style={S.textarea} value={p.description} onChange={ev => updatePathway(p.id, 'description', ev.target.value)} placeholder="Describe what this pathway includes…" rows={2} />
                </div>
              ))}
            </div>
            <button style={S.primaryBtn} onClick={saveAcademy}>Save Changes</button>
          </div>
        </div>
      )}

      {activeTab === "settings" && config && (
        <div style={S.adminBody}>
          <div style={S.adminMain}>
            <h3 style={S.adminAcademyTitle}>Global Settings</h3>
            <div style={S.card}>
              <div style={S.cardLabel}>Academic Year</div>
              <p style={{ color: '#6b7a99', fontSize: 13, marginBottom: 12 }}>Displayed on all headteacher-facing pages.</p>
              <input style={{ ...S.input, maxWidth: 200 }} value={config.academicYear || ''} onChange={e => setConfig(c => ({ ...c, academicYear: e.target.value }))} placeholder="e.g. 2025-26" />
            </div>
            <div style={S.card}>
              <div style={S.cardHeaderRow}>
                <div style={S.cardLabel}>Academy Names</div>
                <button style={S.ghostBtn} onClick={addAcademy}>+ Add Academy</button>
              </div>
              <p style={{ color: '#6b7a99', fontSize: 13, marginBottom: 16 }}>Rename or add academies. Removing hides from headteachers but preserves data.</p>
              {config.academies.map((a, i) => (
                <div key={i} style={S.itemRow}>
                  <input style={{ ...S.input, flex: 1 }} value={a} onChange={e => updateAcademyName(i, e.target.value)} placeholder="Academy name" />
                  <button style={S.iconBtn} onClick={() => removeAcademy(i)}>✕</button>
                </div>
              ))}
            </div>
            <button style={S.primaryBtn} onClick={saveConfig}>Save Settings</button>
          </div>
        </div>
      )}

      {activeTab === "access" && config && (
        <div style={S.adminBody}>
          <div style={S.adminMain}>
            <h3 style={S.adminAcademyTitle}>User Access Control</h3>
            <div style={S.card}>
              <div style={S.cardLabel}>Admin Accounts</div>
              {(config.admins || []).map((email, i) => (
                <div key={i} style={S.itemRow}>
                  <input style={{ ...S.input, flex: 1 }} value={email}
                    onChange={e => { const a = [...config.admins]; a[i] = e.target.value.toLowerCase(); setConfig(c => ({ ...c, admins: a })); }}
                    placeholder="email@astoncetrust.org" />
                  <button style={S.iconBtn} onClick={() => setConfig(c => ({ ...c, admins: c.admins.filter((_, j) => j !== i) }))}>✕</button>
                </div>
              ))}
              <button style={S.ghostBtn} onClick={() => setConfig(c => ({ ...c, admins: [...(c.admins || []), ''] }))}>+ Add Admin</button>
            </div>
            <div style={S.card}>
              <div style={S.cardLabel}>Headteacher Access</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, marginBottom: 10 }}>
                <span style={S.colHead}>Email</span>
                <span style={S.colHead}>Academy</span>
                <span />
              </div>
              {Object.entries(config.academyMapping || {}).map(([email, academy]) => (
                <div key={email} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, marginBottom: 8 }}>
                  <input style={S.input} value={email}
                    onChange={e => { const m = { ...config.academyMapping }; delete m[email]; m[e.target.value.toLowerCase()] = academy; setConfig(c => ({ ...c, academyMapping: m })); }}
                    placeholder="ht@academy.org.uk" />
                  <select style={{ ...S.input, cursor: 'pointer' }} value={academy}
                    onChange={e => setConfig(c => ({ ...c, academyMapping: { ...c.academyMapping, [email]: e.target.value } }))}>
                    {academies.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                  <button style={S.iconBtn} onClick={() => { const m = { ...config.academyMapping }; delete m[email]; setConfig(c => ({ ...c, academyMapping: m })); }}>✕</button>
                </div>
              ))}
              <button style={S.ghostBtn} onClick={() => setConfig(c => ({ ...c, academyMapping: { ...c.academyMapping, '': academies[0] || '' } }))}>+ Add Headteacher</button>
            </div>
            <button style={S.primaryBtn} onClick={saveConfig}>Save Access Settings</button>
          </div>
        </div>
      )}

      {activeTab === "submissions" && (
        <div style={S.adminBody}>
          <div style={S.adminSidebar}>
            <div style={S.sidebarHead}>Filter</div>
            {["All", ...academies].map(a => (
              <button key={a} style={{ ...S.sidebarItem, ...(filterAcademy === a ? S.sidebarItemActive : {}) }} onClick={() => setFilterAcademy(a)}>{a}</button>
            ))}
          </div>
          <div style={S.adminMain}>
            <h3 style={S.adminAcademyTitle}>{filterAcademy === 'All' ? 'All Submissions' : `${filterAcademy}`}</h3>
            {visibleSubmissions.length === 0
              ? <p style={{ color: '#6b7a99', fontFamily: "'Open Sans', sans-serif" }}>No submissions yet.</p>
              : visibleSubmissions.map(s => (
                <div key={s.id} style={S.submissionCard}>
                  <div style={S.submissionCardAccent} />
                  <div style={S.submissionMeta}>
                    <span style={S.submissionAcademy}>{s.academy}</span>
                    <span style={S.submissionDate}>{new Date(s.timestamp).toLocaleString('en-GB')}</span>
                  </div>
                  <div style={S.submissionChoice}>Pathway: <strong>{s.pathwayLabel}</strong> — <span style={{ color: '#1a7a4a', fontWeight: 700 }}>{fmt(s.pathwayCost)}</span></div>
                  {s.notes && <div style={S.submissionNotes}>"{s.notes}"</div>}
                  <div style={S.submissionContact}>{s.submittedBy} · {s.submittedByEmail}</div>
                  <div style={{ marginTop: 12 }}>
                    {resetConfirm === s.id ? (
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <span style={{ fontSize: 13, color: '#c0392b' }}>Reset this submission?</span>
                        <button style={S.dangerBtn} onClick={() => deleteSubmission(s.id)}>Yes, reset</button>
                        <button style={S.ghostBtn} onClick={() => setResetConfirm(null)}>Cancel</button>
                      </div>
                    ) : (
                      <button style={S.resetBtn} onClick={() => setResetConfirm(s.id)}>↺ Reset submission</button>
                    )}
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

function AcademyView({ name, user, onBack, showBack }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: user.name || '', email: user.email || '', notes: '' });
  const [formErr, setFormErr] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [mySubmission, setMySubmission] = useState(null);

  useEffect(() => {
    api.get(`/api/academy/${encodeURIComponent(name)}`).then(setData).catch(() => {});
    api.get('/api/submissions').then(subs => {
      const existing = subs.find(s => s.academy === name && s.submittedByEmail === user.email);
      if (existing) setMySubmission(existing);
    }).catch(() => {});
  }, [name, user.email]);

  const totalEssentials = data ? data.essentials.reduce((s, e) => s + (e.cost || 0), 0) : 0;
  const remaining = data ? (data.dfcAllocation || 0) - totalEssentials : 0;

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your name";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) errs.email = "Please enter a valid email";
    if (!selected) errs.pathway = "Please select a pathway";
    return errs;
  };

  const submit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setFormErr(errs); return; }
    setSubmitting(true);
    const pathway = data.pathways.find(p => p.id === selected);
    try {
      const result = await api.post('/api/submissions', { academy: name, pathwayLabel: pathway.label, pathwayCost: pathway.cost, submittedBy: form.name, notes: form.notes });
      setMySubmission(result.submission);
    } catch { alert('Submission failed. Please try again.'); }
    setSubmitting(false);
  };

  if (!data) return <LoadingScreen />;
  const pathway = data.pathways.find(p => p.id === selected);

  if (mySubmission) {
    return (
      <div style={S.page}>
        <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@700;800&family=Open+Sans:wght@400;500;600&display=swap" rel="stylesheet" />
        <TopBar user={user} showAdmin={false} onAdminClick={() => {}} />
        <div style={S.successPage}>
          <div style={S.successCard}>
            <div style={S.successIconWrap}><div style={S.successIcon}>✓</div></div>
            <h2 style={S.successTitle}>Preference Submitted</h2>
            <p style={S.successAcademy}>{name}</p>
            <div style={S.successTable}>
              {[
                ['Academic Year', user.academicYear],
                ['Pathway chosen', mySubmission.pathwayLabel],
                ['Cost', fmt(mySubmission.pathwayCost)],
                ['Submitted by', mySubmission.submittedBy],
                ['Date', new Date(mySubmission.timestamp).toLocaleString('en-GB')],
              ].map(([k, v]) => (
                <div key={k} style={S.successRow}>
                  <span style={S.successRowKey}>{k}</span>
                  <span style={S.successRowVal}>{v}</span>
                </div>
              ))}
            </div>
            <p style={S.successNote}>Your IT team has been notified and will be in touch to confirm next steps.</p>
            {showBack && <button style={S.primaryBtn} onClick={onBack}>← Return to academies</button>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@700;800&family=Open+Sans:wght@400;500;600&display=swap" rel="stylesheet" />
      <TopBar user={user} showAdmin={false} onAdminClick={() => {}} />

      <div style={S.academyHeroStrip}>
        <div style={S.heroGeoLeft} />
        <div style={S.heroGeoRight} />
        <div style={S.heroContent}>
          {showBack && <button style={S.heroBackBtn} onClick={onBack}>← All Academies</button>}
          <div style={S.heroBadge}>DFC Budget · {user.academicYear}</div>
          <h1 style={S.heroHeading}>{name}</h1>
        </div>
        <div style={S.allocationPill}>
          <div style={S.allocationPillLabel}>Total DFC Allocation</div>
          <div style={S.allocationPillValue}>{fmt(data.dfcAllocation)}</div>
        </div>
      </div>

      <div style={S.pageContent}>
        <div style={S.twoCol}>
          <div style={S.mainCol}>

            <div style={S.card}>
              <div style={S.cardGoldAccent} />
              <h3 style={S.cardTitle}>Essential Repair Costs</h3>
              <p style={S.cardDesc}>Committed costs deducted from your DFC allocation.</p>
              {data.essentials.length === 0
                ? <p style={{ color: '#6b7a99', fontSize: 14 }}>No essential costs configured yet.</p>
                : data.essentials.map(e => (
                  <div key={e.id} style={S.essentialRow}>
                    <span style={S.essentialLabel}>{e.label}</span>
                    <span style={S.essentialAmt}>{fmt(e.cost)}</span>
                  </div>
                ))
              }
              <div style={S.essentialTotal}>
                <span>Total Essentials</span>
                <span style={{ color: '#c0392b', fontWeight: 700 }}>{fmt(totalEssentials)}</span>
              </div>
            </div>

            <div style={S.card}>
              <div style={S.cardGoldAccent} />
              <h3 style={S.cardTitle}>Choose a Pathway</h3>
              <p style={S.cardDesc}>Select how you'd like to allocate your remaining budget.</p>
              {formErr.pathway && <p style={S.errMsg}>{formErr.pathway}</p>}
              <div style={S.pathwayGrid}>
                {data.pathways.map(p => {
                  const affordable = p.cost <= remaining;
                  const sel = selected === p.id;
                  return (
                    <button key={p.id} style={{ ...S.pathwayCard, ...(sel ? S.pathwayCardSel : {}), ...(!affordable ? S.pathwayCardNA : {}) }}
                      onClick={() => { setSelected(p.id); setFormErr(e => ({ ...e, pathway: undefined })); }}>
                      {sel && <div style={S.pathwaySelBadge}>✓ Selected</div>}
                      <div style={S.pathwayCardTop}>
                        <span style={S.pathwayCardLabel}>{p.label}</span>
                        <span style={{ ...S.pathwayCostChip, background: sel ? '#f5a800' : affordable ? '#e8f4ed' : '#fde8e8', color: sel ? '#1a2744' : affordable ? '#1a7a4a' : '#c0392b' }}>{fmt(p.cost)}</span>
                      </div>
                      <p style={S.pathwayCardDesc}>{p.description || 'No description provided.'}</p>
                      {!affordable && <p style={S.pathwayNA}>⚠ Exceeds remaining budget</p>}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={S.card}>
              <div style={S.cardGoldAccent} />
              <h3 style={S.cardTitle}>Submit Your Preference</h3>
              {pathway && <div style={S.selectionBanner}>Selected: <strong>{pathway.label}</strong> — {fmt(pathway.cost)}</div>}
              <div style={S.formGrid}>
                <div>
                  <label style={S.formLabel}>Your Name *</label>
                  <input style={{ ...S.input, ...(formErr.name ? S.inputErr : {}) }} value={form.name}
                    onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setFormErr(fe => ({ ...fe, name: undefined })); }} placeholder="e.g. Jane Smith" />
                  {formErr.name && <p style={S.errMsg}>{formErr.name}</p>}
                </div>
                <div>
                  <label style={S.formLabel}>Email Address *</label>
                  <input style={{ ...S.input, ...(formErr.email ? S.inputErr : {}) }} value={form.email} type="email"
                    onChange={e => { setForm(f => ({ ...f, email: e.target.value })); setFormErr(fe => ({ ...fe, email: undefined })); }} placeholder="headteacher@academy.org.uk" />
                  {formErr.email && <p style={S.errMsg}>{formErr.email}</p>}
                </div>
              </div>
              <label style={S.formLabel}>Additional Notes (optional)</label>
              <textarea style={S.textarea} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Any questions or comments for the IT team…" rows={3} />
              <button style={S.primaryBtn} onClick={submit} disabled={submitting}>{submitting ? 'Submitting…' : 'Submit Preference →'}</button>
            </div>

          </div>

          {/* Sidebar summary */}
          <div style={S.sideCol}>
            <div style={S.summaryCard}>
              <div style={S.summaryCardTop}>
                <div style={S.summaryLabel}>DFC Allocation</div>
                <div style={S.summaryValue}>{fmt(data.dfcAllocation)}</div>
              </div>
              <div style={S.summaryDivider} />
              <div style={S.summaryRow}>
                <span>Essential costs</span>
                <span style={{ color: '#c0392b', fontWeight: 600 }}>− {fmt(totalEssentials)}</span>
              </div>
              <div style={S.summaryDivider} />
              <div style={S.summaryRemainingRow}>
                <span style={S.summaryRemainingLabel}>Remaining</span>
                <span style={{ ...S.summaryRemainingValue, color: remaining >= 0 ? '#1a7a4a' : '#c0392b' }}>{fmt(remaining)}</span>
              </div>
              {pathway && (
                <>
                  <div style={S.summaryDivider} />
                  <div style={S.summaryRow}>
                    <span>Selected pathway</span>
                    <span style={{ color: '#1a2744', fontWeight: 700 }}>{pathway.label}</span>
                  </div>
                  <div style={S.summaryRow}>
                    <span>Pathway cost</span>
                    <span style={{ fontWeight: 600 }}>− {fmt(pathway.cost)}</span>
                  </div>
                  <div style={S.summaryDivider} />
                  <div style={S.summaryRemainingRow}>
                    <span style={S.summaryRemainingLabel}>After pathway</span>
                    <span style={{ ...S.summaryRemainingValue, fontSize: 20, color: (remaining - pathway.cost) >= 0 ? '#1a7a4a' : '#c0392b' }}>{fmt(remaining - pathway.cost)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  page: { minHeight: '100vh', background: '#f4f6fb', fontFamily: "'Open Sans', sans-serif", color: '#1a2744' },

  loadingWrap: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f4f6fb', gap: 16 },
  spinner: { width: 32, height: 32, border: '3px solid #d0d8ea', borderTop: '3px solid #f5a800', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },

  // Login
  loginPage: { minHeight: '100vh', display: 'flex' },
  loginLeft: { width: '40%', background: '#1a2744', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  loginGeo: { position: 'absolute', bottom: -80, right: -60, width: 320, height: 320, background: '#f5a800', opacity: 0.15, transform: 'rotate(45deg)' },
  loginGeo2: { position: 'absolute', top: -60, left: -40, width: 200, height: 200, background: '#f5a800', opacity: 0.08, transform: 'rotate(30deg)' },
  loginLeftContent: { position: 'relative', textAlign: 'center', color: '#fff' },
  loginBrand: { fontFamily: "'Montserrat', sans-serif", fontSize: 48, fontWeight: 800, color: '#fff', margin: '16px 0 4px', letterSpacing: '0.05em' },
  loginBrandSub: { color: '#8ba4c8', fontSize: 13, margin: 0 },
  loginRight: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, background: '#f4f6fb' },
  loginCard: { background: '#fff', borderRadius: 16, padding: '48px 40px', width: '100%', maxWidth: 420, boxShadow: '0 4px 32px #1a274414', border: '1px solid #d0d8ea' },
  loginCardBadge: { display: 'inline-block', background: '#f5a80020', border: '1px solid #f5a80060', color: '#b07800', borderRadius: 4, padding: '3px 10px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 16 },
  loginTitle: { fontFamily: "'Montserrat', sans-serif", fontSize: 28, fontWeight: 800, margin: '0 0 8px', color: '#1a2744' },
  loginSubtitle: { color: '#6b7a99', fontSize: 14, margin: '0 0 28px', lineHeight: 1.6 },
  loginNote: { color: '#9aa3b5', fontSize: 12, textAlign: 'center', marginTop: 12, margin: '12px 0 0' },
  errorBanner: { background: '#fde8e8', border: '1px solid #f5c6c6', color: '#c0392b', borderRadius: 8, padding: '10px 16px', fontSize: 13, marginBottom: 16 },
  msBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', color: '#333', border: '1px solid #d0d8ea', borderRadius: 8, padding: '13px 24px', fontSize: 15, fontWeight: 600, textDecoration: 'none', cursor: 'pointer', boxShadow: '0 1px 4px #1a274410', transition: 'all 0.2s' },

  // Top bar
  topBar: { background: '#1a2744', borderBottom: '3px solid #f5a800', padding: '0 32px' },
  topBarInner: { maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 60 },
  topBarLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  topBarTitle: { display: 'flex', flexDirection: 'column' },
  topBarACET: { fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: 16, color: '#fff', letterSpacing: '0.08em' },
  topBarSub: { fontSize: 11, color: '#8ba4c8', letterSpacing: '0.04em' },
  topBarRight: { display: 'flex', alignItems: 'center', gap: 16 },
  topBarUser: { display: 'flex', alignItems: 'center', gap: 8 },
  userAvatar: { width: 30, height: 30, borderRadius: '50%', background: '#f5a800', color: '#1a2744', fontWeight: 800, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  userName: { color: '#c8d4e8', fontSize: 13, fontWeight: 500 },
  topBarAdminBtn: { background: '#f5a800', border: 'none', color: '#1a2744', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: "'Open Sans', sans-serif" },
  topBarLogout: { color: '#8ba4c8', fontSize: 12, textDecoration: 'none' },

  // Hero strip
  heroStrip: { background: '#1a2744', padding: '40px 32px', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', minHeight: 160 },
  academyHeroStrip: { background: '#1a2744', padding: '32px 32px 28px', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', minHeight: 140 },
  heroGeoLeft: { position: 'absolute', bottom: -40, left: -20, width: 0, height: 0, borderLeft: '120px solid transparent', borderRight: '120px solid transparent', borderBottom: '200px solid #f5a800', opacity: 0.12 },
  heroGeoRight: { position: 'absolute', top: -30, right: 80, width: 0, height: 0, borderLeft: '180px solid transparent', borderRight: '180px solid transparent', borderBottom: '280px solid #f5a800', opacity: 0.08 },
  heroContent: { position: 'relative', zIndex: 1 },
  heroBadge: { display: 'inline-block', background: '#f5a800', color: '#1a2744', borderRadius: 4, padding: '3px 10px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 },
  heroHeading: { fontFamily: "'Montserrat', sans-serif", fontSize: 36, fontWeight: 800, color: '#fff', margin: '0 0 6px' },
  heroDesc: { color: '#8ba4c8', fontSize: 14, margin: 0 },
  heroBackBtn: { background: 'transparent', border: 'none', color: '#8ba4c8', cursor: 'pointer', fontSize: 13, fontFamily: "'Open Sans', sans-serif", padding: '0 0 8px', display: 'block' },
  allocationPill: { position: 'relative', zIndex: 1, background: '#243156', border: '1px solid #3a4f7a', borderRadius: 12, padding: '16px 24px', textAlign: 'right' },
  allocationPillLabel: { fontSize: 11, color: '#8ba4c8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 },
  allocationPillValue: { fontFamily: "'Montserrat', sans-serif", fontSize: 28, fontWeight: 800, color: '#f5a800' },

  pageContent: { maxWidth: 1200, margin: '0 auto', padding: '32px' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 300px', gap: 24, alignItems: 'start' },
  mainCol: { display: 'flex', flexDirection: 'column', gap: 24 },
  sideCol: { position: 'sticky', top: 24 },

  // Academy grid
  academyGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 },
  academyCard: { background: '#fff', border: '1px solid #d0d8ea', borderRadius: 10, padding: '20px 18px', cursor: 'pointer', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 6, transition: 'all 0.2s', fontFamily: "'Open Sans', sans-serif", position: 'relative', overflow: 'hidden' },
  academyCardHover: { boxShadow: '0 8px 24px #1a274420', transform: 'translateY(-2px)', borderColor: '#f5a800' },
  academyCardAccent: { position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#f5a800' },
  academyCardInitial: { width: 40, height: 40, borderRadius: 8, background: '#1a2744', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: '#f5a800', marginBottom: 4, fontFamily: "'Montserrat', sans-serif" },
  academyCardName: { fontSize: 14, fontWeight: 600, color: '#1a2744', flex: 1 },
  academyCardArrow: { color: '#f5a800', fontSize: 18, fontWeight: 700 },

  // Cards
  card: { background: '#fff', border: '1px solid #d0d8ea', borderRadius: 10, padding: '24px', position: 'relative', overflow: 'hidden' },
  cardGoldAccent: { position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #f5a800, #ffc235)' },
  cardTitle: { fontFamily: "'Montserrat', sans-serif", fontSize: 18, fontWeight: 700, color: '#1a2744', margin: '4px 0 6px' },
  cardDesc: { color: '#6b7a99', fontSize: 13, margin: '0 0 16px', lineHeight: 1.5 },
  cardLabel: { fontSize: 11, fontWeight: 700, color: '#1a2744', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14 },
  cardHeaderRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },

  // Essentials
  essentialRow: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #edf0f7' },
  essentialLabel: { fontSize: 14, color: '#3a4f7a' },
  essentialAmt: { fontSize: 14, fontWeight: 600, color: '#1a2744' },
  essentialTotal: { display: 'flex', justifyContent: 'space-between', padding: '12px 0 0', fontSize: 14, marginTop: 4 },

  // Budget summary sidebar
  summaryCard: { background: '#1a2744', borderRadius: 10, padding: '24px', color: '#fff' },
  summaryCardTop: { marginBottom: 16 },
  summaryLabel: { fontSize: 11, color: '#8ba4c8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 },
  summaryValue: { fontFamily: "'Montserrat', sans-serif", fontSize: 28, fontWeight: 800, color: '#f5a800' },
  summaryDivider: { height: 1, background: '#2d4070', margin: '12px 0' },
  summaryRow: { display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#8ba4c8', padding: '4px 0' },
  summaryRemainingRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' },
  summaryRemainingLabel: { fontSize: 13, color: '#8ba4c8' },
  summaryRemainingValue: { fontFamily: "'Montserrat', sans-serif", fontSize: 26, fontWeight: 800 },

  // Pathways
  pathwayGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 },
  pathwayCard: { background: '#f4f6fb', border: '2px solid #d0d8ea', borderRadius: 10, padding: '16px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s', fontFamily: "'Open Sans', sans-serif", position: 'relative' },
  pathwayCardSel: { border: '2px solid #1a2744', background: '#fff', boxShadow: '0 4px 16px #1a274420' },
  pathwayCardNA: { opacity: 0.5 },
  pathwaySelBadge: { position: 'absolute', top: 10, right: 10, background: '#1a2744', color: '#f5a800', borderRadius: 99, padding: '2px 8px', fontSize: 10, fontWeight: 700 },
  pathwayCardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  pathwayCardLabel: { fontSize: 15, fontWeight: 700, color: '#1a2744' },
  pathwayCostChip: { borderRadius: 6, padding: '4px 10px', fontSize: 13, fontWeight: 700 },
  pathwayCardDesc: { fontSize: 12, color: '#6b7a99', lineHeight: 1.6, margin: 0 },
  pathwayNA: { fontSize: 11, color: '#c0392b', marginTop: 8, margin: '8px 0 0' },

  selectionBanner: { background: '#fffbee', border: '1px solid #f5a80060', borderRadius: 8, padding: '10px 14px', fontSize: 14, color: '#7a5800', marginBottom: 16, fontWeight: 500 },

  // Form
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },
  formLabel: { display: 'block', fontSize: 11, fontWeight: 700, color: '#1a2744', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 },
  input: { background: '#f4f6fb', border: '1px solid #d0d8ea', borderRadius: 6, color: '#1a2744', padding: '10px 12px', fontSize: 14, fontFamily: "'Open Sans', sans-serif", width: '100%', boxSizing: 'border-box', outline: 'none' },
  inputErr: { border: '1px solid #c0392b' },
  textarea: { background: '#f4f6fb', border: '1px solid #d0d8ea', borderRadius: 6, color: '#1a2744', padding: '10px 12px', fontSize: 14, fontFamily: "'Open Sans', sans-serif", width: '100%', boxSizing: 'border-box', outline: 'none', resize: 'vertical', display: 'block', marginTop: 6 },
  errMsg: { color: '#c0392b', fontSize: 12, margin: '4px 0 0' },

  primaryBtn: { background: '#1a2744', border: 'none', color: '#fff', padding: '13px 28px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: "'Open Sans', sans-serif", transition: 'background 0.2s' },

  // Admin
  adminTopBar: { background: '#fff', borderBottom: '1px solid #d0d8ea' },
  adminTopBarInner: { maxWidth: 1200, margin: '0 auto', padding: '20px 32px 0', display: 'flex', alignItems: 'center', gap: 24 },
  backBtn: { background: 'transparent', border: 'none', color: '#1a2744', cursor: 'pointer', fontSize: 13, fontFamily: "'Open Sans', sans-serif", padding: 0, fontWeight: 600 },
  adminTopTitle: { fontFamily: "'Montserrat', sans-serif", fontSize: 22, fontWeight: 800, color: '#1a2744', margin: 0 },
  savedToast: { background: '#e8f4ed', border: '1px solid #a8d4b8', color: '#1a7a4a', borderRadius: 6, padding: '6px 14px', fontSize: 13, fontWeight: 600 },
  adminTabRow: { maxWidth: 1200, margin: '0 auto', padding: '0 32px', display: 'flex', marginTop: 16 },
  adminTab: { background: 'transparent', border: 'none', borderBottom: '3px solid transparent', color: '#6b7a99', padding: '10px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: "'Open Sans', sans-serif", transition: 'all 0.15s' },
  adminTabActive: { color: '#1a2744', borderBottom: '3px solid #f5a800' },
  tabBadge: { background: '#1a2744', color: '#f5a800', borderRadius: 99, padding: '1px 7px', fontSize: 10, marginLeft: 6, fontWeight: 700 },
  adminBody: { display: 'flex', maxWidth: 1200, margin: '0 auto', padding: '24px 32px 48px', gap: 24, minHeight: 'calc(100vh - 200px)' },
  adminSidebar: { width: 200, flexShrink: 0 },
  sidebarHead: { fontSize: 10, fontWeight: 700, color: '#6b7a99', textTransform: 'uppercase', letterSpacing: '0.1em', padding: '8px 0', marginBottom: 4 },
  sidebarItem: { display: 'block', width: '100%', background: 'transparent', border: 'none', color: '#3a4f7a', padding: '9px 12px', textAlign: 'left', cursor: 'pointer', fontSize: 13, fontFamily: "'Open Sans', sans-serif", borderLeft: '3px solid transparent', borderRadius: '0 6px 6px 0', transition: 'all 0.15s' },
  sidebarItemActive: { color: '#1a2744', borderLeft: '3px solid #f5a800', background: '#fffbee', fontWeight: 600 },
  adminMain: { flex: 1, display: 'flex', flexDirection: 'column', gap: 20 },
  adminAcademyHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 },
  adminAcademyAccent: { width: 4, height: 28, background: '#f5a800', borderRadius: 2 },
  adminAcademyTitle: { fontFamily: "'Montserrat', sans-serif", fontSize: 22, fontWeight: 800, color: '#1a2744', margin: 0 },
  itemRow: { display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' },
  pathwayEditCard: { background: '#f4f6fb', borderRadius: 8, padding: 14, marginBottom: 12, border: '1px solid #d0d8ea' },
  totalLine: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontSize: 14, borderTop: '1px solid #edf0f7', marginTop: 8 },
  ghostBtn: { background: 'transparent', border: '1px solid #d0d8ea', color: '#1a2744', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: "'Open Sans', sans-serif" },
  iconBtn: { background: '#fde8e8', border: '1px solid #f5c6c6', color: '#c0392b', width: 32, height: 32, borderRadius: 6, cursor: 'pointer', fontSize: 13, flexShrink: 0 },
  colHead: { fontSize: 10, fontWeight: 700, color: '#6b7a99', textTransform: 'uppercase', letterSpacing: '0.08em' },

  // Submissions
  submissionCard: { background: '#fff', border: '1px solid #d0d8ea', borderRadius: 10, padding: '18px 20px', marginBottom: 14, position: 'relative', overflow: 'hidden' },
  submissionCardAccent: { position: 'absolute', top: 0, left: 0, bottom: 0, width: 4, background: '#f5a800' },
  submissionMeta: { display: 'flex', justifyContent: 'space-between', marginBottom: 8, paddingLeft: 8 },
  submissionAcademy: { fontWeight: 700, color: '#1a2744', fontSize: 14, fontFamily: "'Montserrat', sans-serif" },
  submissionDate: { fontSize: 12, color: '#6b7a99' },
  submissionChoice: { fontSize: 14, color: '#3a4f7a', marginBottom: 4, paddingLeft: 8 },
  submissionNotes: { fontSize: 13, color: '#6b7a99', fontStyle: 'italic', paddingLeft: 8, marginBottom: 4 },
  submissionContact: { fontSize: 12, color: '#9aa3b5', paddingLeft: 8 },
  resetBtn: { background: '#fde8e8', border: '1px solid #f5c6c6', color: '#c0392b', padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: "'Open Sans', sans-serif" },
  dangerBtn: { background: '#c0392b', border: 'none', color: '#fff', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: "'Open Sans', sans-serif" },

  // Success
  successPage: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, minHeight: 'calc(100vh - 63px)' },
  successCard: { background: '#fff', border: '1px solid #d0d8ea', borderRadius: 16, padding: '48px 40px', width: '100%', maxWidth: 500, textAlign: 'center', boxShadow: '0 4px 32px #1a274414' },
  successIconWrap: { marginBottom: 20 },
  successIcon: { width: 60, height: 60, borderRadius: '50%', background: '#e8f4ed', border: '2px solid #a8d4b8', color: '#1a7a4a', fontSize: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', fontWeight: 700 },
  successTitle: { fontFamily: "'Montserrat', sans-serif", fontSize: 26, fontWeight: 800, margin: '0 0 4px', color: '#1a2744' },
  successAcademy: { color: '#6b7a99', fontSize: 14, margin: '0 0 24px' },
  successTable: { background: '#f4f6fb', borderRadius: 10, padding: '4px 20px', marginBottom: 24, textAlign: 'left' },
  successRow: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #edf0f7', fontSize: 14 },
  successRowKey: { color: '#6b7a99' },
  successRowVal: { fontWeight: 700, color: '#1a2744' },
  successNote: { color: '#6b7a99', fontSize: 13, lineHeight: 1.6, margin: '0 0 24px' },

  backLink: { background: 'transparent', border: 'none', color: '#1a2744', cursor: 'pointer', fontSize: 13, fontFamily: "'Open Sans', sans-serif", padding: 0, fontWeight: 600 },
};
